import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Skeleton } from "../components/ui/skeleton";
import { Plus, Search, MoreHorizontal, Eye, FileCheck, Filter, ShoppingCart } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const StatusBadge = ({ status }) => {
  const config = {
    PENDING: "status-pending",
    APPROVED: "status-approved",
    REJECTED: "status-rejected",
    RETURNED: "status-returned",
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config[status] || "status-draft"}`}>
      {status}
    </span>
  );
};

const SanctionProposal = () => {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchProposals();
  }, []);

  const fetchProposals = async () => {
    try {
      const response = await axios.get(`${API_URL}/sanction-proposals`, getAuthHeader());
      setProposals(response.data);
    } catch (error) {
      toast.error("Failed to fetch sanction proposals");
    } finally {
      setLoading(false);
    }
  };

  const filteredProposals = proposals.filter(p =>
    p.proposal_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.requisition_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="sanction-proposals-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Purchase Sanction Proposals</h1>
          <p className="text-slate-500 mt-1">Manage purchase sanction proposals for final approval</p>
        </div>
        {user?.role === "INDENTER" && (
          <Button 
            onClick={() => navigate("/sanction-proposals/new")}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="new-proposal-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Sanction Proposal
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search proposals..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-proposals"
              />
            </div>
            <Button variant="outline" className="gap-2">
              <Filter className="w-4 h-4" />
              Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>
          ) : filteredProposals.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Proposal Number</TableHead>
                  <TableHead className="font-semibold">Requisition</TableHead>
                  <TableHead className="font-semibold">Purpose</TableHead>
                  <TableHead className="font-semibold">Final Price</TableHead>
                  <TableHead className="font-semibold">Currency</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProposals.map((proposal) => (
                  <TableRow 
                    key={proposal.id} 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/sanction-proposals/${proposal.id}`)}
                    data-testid={`proposal-row-${proposal.id}`}
                  >
                    <TableCell className="font-medium text-slate-900">
                      {proposal.proposal_number}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {proposal.requisition_number}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        proposal.purpose === "SCIENTIFIC" 
                          ? "bg-purple-50 text-purple-700 border border-purple-200" 
                          : "bg-blue-50 text-blue-700 border border-blue-200"
                      }`}>
                        {proposal.purpose}
                      </span>
                    </TableCell>
                    <TableCell className="font-semibold text-slate-900">
                      ₹{proposal.final_price.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="text-slate-600">{proposal.currency}</TableCell>
                    <TableCell>
                      <StatusBadge status={proposal.status} />
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {new Date(proposal.created_at).toLocaleDateString('en-IN')}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/sanction-proposals/${proposal.id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {proposal.status === "APPROVED" && (
                            <DropdownMenuItem onClick={() => navigate(`/purchase-orders/new?proposal=${proposal.id}`)}>
                              <ShoppingCart className="w-4 h-4 mr-2" />
                              Create PO
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-12 text-center">
              <FileCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 font-medium">No sanction proposals found</p>
              <p className="text-sm text-slate-400 mb-4">
                {searchTerm ? "Try a different search term" : "Create a sanction proposal after requisition approval"}
              </p>
              {user?.role === "INDENTER" && !searchTerm && (
                <Button onClick={() => navigate("/sanction-proposals/new")} data-testid="empty-state-new-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Sanction Proposal
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      {!loading && proposals.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <p className="text-sm text-slate-500">Total</p>
            <p className="text-2xl font-bold text-slate-900">{proposals.length}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-slate-500">Pending</p>
            <p className="text-2xl font-bold text-amber-600">
              {proposals.filter(p => p.status === "PENDING").length}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-slate-500">Approved</p>
            <p className="text-2xl font-bold text-emerald-600">
              {proposals.filter(p => p.status === "APPROVED").length}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-slate-500">Total Value</p>
            <p className="text-2xl font-bold text-slate-900">
              ₹{proposals.reduce((sum, p) => sum + p.final_price, 0).toLocaleString('en-IN')}
            </p>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SanctionProposal;
